import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { ErrorMessage, useField } from 'formik'
import React, { useCallback, useState } from 'react'
import toastr from 'toastr'
import cloudinaryTools, { CloudinaryUploadFolders } from '../../../util/cloudinaryTools'
import { UploadState, UploadStateType } from '../../../util/constants'
import { IconGlyph } from '../../../icons/IconGlyph'
import FileDropBox from '../misc/FileDropBox'
import ResponsiveImage, { VideoTag } from '../misc/MediaPresets'

interface MediaUploadFieldProps {
  className?: string
  label?: string
  name: string
  folder: CloudinaryUploadFolders
  onUploadingStateChange?(...args: unknown[]): unknown
  allowVideo: boolean
}

const MediaUploadField: React.FC<MediaUploadFieldProps> = props => {
  const { allowVideo = true, folder = 'uploads', label, name, onUploadingStateChange } = props
  const [uploadState, setUploadState] = useState<UploadStateType>(UploadState.IDLE)

  // skip meta field, we don't need it
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, _meta, helpers] = useField(name)
  const value = field.value
  const onChange = helpers.setValue

  const handleRemoveMedia = useCallback(() => {
    // Cloudinary.delete(value.publicId, (err, res) => {});
    // don't wait until Cloudinary API removes the media

    // width/height describe the file that was just removed, so they go with
    // it. Explicit nulls, not omitted keys: the submit payload flattens this
    // object, and the mutation only clears a column for a field it receives.
    if (allowVideo) {
      onChange({ publicId: '', type: '', width: null, height: null })
    } else {
      onChange({ publicId: '', width: null, height: null })
    }

    setUploadState(UploadState.IDLE)
  }, [allowVideo, onChange, setUploadState])

  const uploadMediaToCloudinary = useCallback(
    async (file: File) => {
      if (file == null) return

      const fileIsVideo = file.type.indexOf('video') === 0

      let newUploadState: UploadStateType = UploadState.UPLOADING
      setUploadState(newUploadState)

      onUploadingStateChange && onUploadingStateChange(newUploadState)

      const options = { folder, resource_type: '' }
      if (fileIsVideo) options.resource_type = 'video'

      await cloudinaryTools.upload(file, options, (error, response) => {
        newUploadState = error ? UploadState.ERROR : UploadState.SUCCESS
        setUploadState(newUploadState)

        onUploadingStateChange && onUploadingStateChange(newUploadState)

        if (error) {
          toastr.error(error)

          allowVideo
            ? onChange({ publicId: '', type: '', width: null, height: null })
            : onChange({ publicId: '', width: null, height: null })
        } else {
          // Cloudinary reports the stored file's true pixel size in the upload
          // response. Carrying it through is what lets a detail page reserve
          // the image's exact box before the file downloads, instead of
          // reflowing when it lands. Videos have dimensions too; only events
          // read them today. Nulls (not undefined) so replacing a known image
          // with one Cloudinary sized oddly clears the previous numbers rather
          // than leaving them to describe the wrong file.
          const width = response.width ?? null
          const height = response.height ?? null
          if (allowVideo) {
            onChange({ publicId: response.public_id, type: fileIsVideo ? 'video' : 'image', width, height })
          } else {
            onChange({ publicId: response.public_id, width, height })
          }
        }
      })
    },
    [allowVideo, folder, onChange, onUploadingStateChange],
  )

  const { publicId, type } = value

  const className = props.className ? `${props.className} file-upload` : 'file-upload'

  return (
    <StyledUploadBox
      className={className}
      onDraggingStyle={css`
        background-color: white;
      `}
      onDropped={uploadMediaToCloudinary}
    >
      {publicId ? (
        <UploadedMediaPreview onRemove={handleRemoveMedia} type={type} publicId={publicId} />
      ) : (
        <StyledUploadBoxLabel htmlFor={`imgInp-${name}`}>
          <div>{label}</div>
          <FileSelect
            id={`imgInp-${name}`}
            accept={allowVideo ? 'image/*,video/*' : 'image/*'}
            uploadState={uploadState}
            onFileSelected={uploadMediaToCloudinary}
          />
        </StyledUploadBoxLabel>
      )}
      <ErrorMessage name={name} />
    </StyledUploadBox>
  )
}

const StyledUploadBox = styled(FileDropBox)`
  height: 100%;
  width: 100%;
  & > * {
    padding: 15px;
  }
  border-radius: 5px;
  border: 1px dashed #959595;
`

const UploadedMediaPreview: React.FC<{ onRemove?: () => void; type: string; publicId: string }> = props => {
  const { type, publicId, onRemove } = props
  return (
    <MediaPreviewBox className="media-preview">
      {type === 'video' ? <VideoTag publicId={publicId} /> : <ResponsiveImage publicId={publicId} />}
      <RemoveMediaButton onClick={onRemove} />
    </MediaPreviewBox>
  )
}

const MediaPreviewBox = styled.div`
  position: relative;
  height: 100%;
  img {
    object-fit: contain;
    height: 100%;
    width: 100%;
  }
`

const RemoveMediaButton: React.FC<{ onClick?: (e) => void }> = props => (
  <TopRightButton type="button" onClick={props.onClick}>
    <RedXIcon name="cancel" />
  </TopRightButton>
)

const StyledUploadBoxLabel = styled.label`
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const FileSelect: React.FC<{ onFileSelected: (file: File) => void; uploadState: UploadStateType; id: string; accept: string }> = props => {
  return (
    <StyledFileSelect>
      <IconGlyph
        name="upload"
        css={css`
          margin: 10px;
        `}
      />
      <input
        type="file"
        id={props.id}
        accept={props.accept}
        onChange={e => {
          const file = e.currentTarget.files ? e.currentTarget.files[0] : null
          props.onFileSelected(file)
        }}
      />
      <UploadStateMessage uploadState={props.uploadState} />
    </StyledFileSelect>
  )
}

const StyledFileSelect = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  text-align: center;

  .upload-status {
    font-weight: normal;
  }
  input[type='file'] {
    display: none;
  }
`

const UploadStateMessage: React.FC<{ uploadState: string }> = ({ uploadState }) => {
  switch (uploadState) {
    case UploadState.UPLOADING:
      return <div className="upload-status">Uploading...</div>

    case UploadState.SUCCESS:
      return <div className="upload-status success">Uploaded</div>

    case UploadState.ERROR:
      return <div className="upload-status error">Upload error</div>

    default:
      return <div className="upload-status">Drag and drop or upload from your computer.</div>
  }
}

// make it easier to hit with the finger with 30x30px
const TopRightButton = styled.button`
  margin-right: 0;
  margin-top: 0;
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 0;
  width: 30px;
  height: 30px;
  background-color: transparent;
`

const RedXIcon = styled(IconGlyph)`
  margin: 0;
  background-color: white;
  color: #d95859;
  font-size: 16px;
`

export default MediaUploadField
